// NotesApp source file: project-cards.component.ts

import { NoteDisplayTitlePipe } from '../../../_util/note-display-title.util';
import {
  ComponentFactoryResolver,
  Input,
  Injector,
  ApplicationRef,
  AfterViewInit,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  ViewContainerRef,
  computed,
  inject,
  HostListener,
} from '@angular/core';

import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { filter, take } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuItem, MessageService } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Notes-App Services
import {
  Note,
  Project,
  Status,
  NoteType,
  DataService,
} from '../../../_service/data.service';
import { LayoutService } from '../../../_service/layout.service';
import { AlertService } from '../../../_service/alert.service';
import { LogService } from '../../../_service/log.service';
import { FilterParams, FilterService } from '../../../_service/filter.service';
import { isEmphasizedStatus, isMutedStatus } from '../../../_util/note-status.util';

type CardSort = {
  project_id: number;
  sort_order: number;
};

@Component({
  selector: 'app-project-cards',
  imports: [
    CommonModule,
    NoteDisplayTitlePipe,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './project-cards.component.html',
  styleUrls: ['./project-cards.component.scss'],
  standalone: true,
})


export class ProjectCardsComponent implements OnInit, OnDestroy {
  readonly isEmphasizedStatus = isEmphasizedStatus;
  readonly isMutedStatus = isMutedStatus;
  @Input() notes: Note[] = [];
  @Input() projects: Project[] = [];
  @Input() filter: FilterParams = {};

  data: Note[] = [];
  paginatedData: Note[] = [];
  currentPage: number = 1;
  pageSize: number = 10;
  sortBy: string = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  //projects: Project[] | undefined;
  card_sort: CardSort[] = [];
  statusValues: string[] | undefined;
  types: string[] | undefined;
  isHeaderVisible: boolean = true;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  loadingCounter = 0; // initially count of loading objects in progress
  isOnline: boolean = true; //??

  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  synchLog$: Observable<string[]>;

  constructor(
    private filterService: FilterService,
    private dataService: DataService,
    private route: ActivatedRoute,
    private router: Router,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private alertService: AlertService,
    private logs: LogService,
    private messages: MessageService,
  ) {
    this.synchLog$ = this.logs.entries$('synch');
  }

  async ngOnInit() {
    console.log('ProjectCardsComponent - in ngOnInit()');
  }

  ngOnDestroy() {}

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  toggleDarkMode() {
    this.layoutService.appState.update((state) => ({
      ...state,
      darkMode: !state.darkMode,
    }));
  }

  DEBUG_printAssignedSortOrders(projects: Project[] | undefined, card_sort: CardSort[]) {
    //console.log("Assigned Sort Orders:");
    card_sort.forEach((card) => {
      //   const project = projects.find(p => p.ID === card.project_id);
      const project = (projects ?? []).find((p) => p.ID === card.project_id);
      if (project) {
        console.log(`Project: ${project.Name}, Sort Order: ${card.sort_order}`);
      } else {
        console.log(`Project not found for project_id: ${card.project_id}`);
      }
    });
  }

  assignSortOrderToProjects(
    projects: Project[] | undefined,
    noteItems: Note[],
    card_sort: CardSort[],
  ) {
    // First, filter out the projects that already have a sort order assigned in card_sort
    const projectHasSort = new Set(card_sort.map((cs) => cs.project_id));
    // console.log(`projectHasSort: ${[...projectHasSort]}`);

    // Filter note-items that belong to projects that haven't been sorted yet
    const unsortedNoteItems = noteItems.filter((note) => !projectHasSort.has(note.Project));

    // Sort the note-items by last_modified in descending order (latest first)
    const sortedNoteItems = unsortedNoteItems.sort((a, b) => {
      return new Date(b.last_modified).getTime() - new Date(a.last_modified).getTime();
    });

    // Start assigning sort orders, beginning with the value after the last assigned sort order
    let nextSortValue = card_sort.length + 1;

    // Track projects that have been assigned a sort order in this function call
    const assignedProjects = new Set<number>();

    // Assign sort order based on the sorted note-items
    sortedNoteItems.forEach((note) => {
      const project = (projects ?? []).find((p) => p.ID === note.Project);

      if (project && !projectHasSort.has(project.ID) && !assignedProjects.has(project.ID)) {
        // Add the project to the card_sort with the next sort value
        card_sort.push({ project_id: project.ID, sort_order: nextSortValue });
        nextSortValue++;
        // Mark the project as assigned
        assignedProjects.add(project.ID);
      }
    });

    // Optionally: sort the card_sort array by sort_order if needed
    card_sort.sort((a, b) => a.sort_order - b.sort_order);
  }

  sortNotesByCardSort(): void {
    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    this.card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the data array based on the sort_order in card_sort
    this.data.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.Project) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.Project) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });

    // Update paginatedData after sorting
    //this.paginateData();
  }

  sortProjectsByCardSort(): void {
    // Create a map from project_id to sort_order for quick lookup
    const sortOrderMap = new Map<number, number>();
    this.card_sort.forEach((card) => {
      sortOrderMap.set(card.project_id, card.sort_order);
    });

    // Sort the projects array based on the sort_order in card_sort
    this.projects?.sort((a, b) => {
      const sortOrderA = sortOrderMap.get(a.ID) ?? Number.MAX_SAFE_INTEGER;
      const sortOrderB = sortOrderMap.get(b.ID) ?? Number.MAX_SAFE_INTEGER;
      return sortOrderA - sortOrderB;
    });
  }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // If current day is less than the modified day, subtract one (not a full month yet)
    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

  getLastModifiedNotes(projectId: number): Note[] {
    // const notes = this.data.filter(note => note.Project === projectId);
    const notes = this.notes.filter((note) => note.Project === projectId);
    return notes.sort((a, b) => {
      const dateA = new Date(a.last_modified);
      const dateB = new Date(b.last_modified);
      return dateB.getTime() - dateA.getTime(); // For descending order
    });
    // }).slice(0, 20);
  }

  // count the number of filtered notes for a project
  calculateItemCount(projectId: number): number {
    return this.notes.filter((note) => note.Project === projectId).length;
    //return 1;xxx
  }

  viewAllNotes(projectId: number): void {
    // this.router.navigate(['/project', projectId]);
  }

  toggleHeaderVisibility(): void {
    //console.log("toggle header now =",!this.isHeaderVisible);
    this.isHeaderVisible = !this.isHeaderVisible;
  }

  clickCardHeader(proj: Project): void {
    console.log('clickCardHeader() - Project: ', proj.Name);
    this.filter.ProjectName = proj.Name;
    this.router
      .navigate(['/list'], {
        state: { filter: this.filter },
      })
      .then(() => {
        console.log('cards:clickCardHeader() - Navigation completed. State sent.');
      });
  }

  clickRow(row: Note) {
    console.log('clickRow() - Note: ', row.Title);
    this.router.navigate(['view', row._id], {
      state: { filter: this.filter },
    });
  }

  isVisible(element: HTMLElement): boolean {
    const style = window.getComputedStyle(element);
    return (
      element.offsetParent !== null && style.visibility !== 'hidden' && style.display !== 'none'
    );
  }

  // ##############################################################################
  // NAVIGATION FUNCTIONS
  // ##############################################################################

  goToListView(): void {
    console.log('goToListView');
    this.router.navigate(['/list'], { state: { filter: this.filter } });
  }

  navigateToEdit(row: Note) {
    console.log(row);
    this.router.navigate(['edit', row._id], {
      state: { filter: this.filter },
    });
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################
}
